"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.constructHistoricalAssetVerificationReport = exports.generateReceivableDocumentationReport = exports.executeAssetVerification = exports.createAssetDocumentationEvaluator = exports.getPartialFieldResult = exports.checkAssetDocumentationFormulaErrors = void 0;
const luxon_1 = require("luxon");
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const BASE_FIELD_PREFIX = 'baseFields';
const DYNAMIC_INTERMEDIATE_PREFIX = 'dynamicIntermediateFields';
const DOCUMENTATION_PREFIX = 'documentation';
/**
 * Asset documentation evaluation formulas are evaluated against the receivable's base fields, dynamic intermediate fields, and the documentation fields. This function will check the formulas for any missing fields.
 * Note: intermediate calcs are only available in CA. these formulas are authored on tape-builder. So when authroing formulas we can only partially verify those (base fields and documentation fields)
 */
const checkAssetDocumentationFormulaErrors = ({ formulas, dynamicIntermediateFields, baseFields, documentationFields, ignoreDynamicIntermediateFields, }) => {
    // we use "." as a separator to determine baseField v/s documentation fields.
    // ensure that seperator does not already exists in baseFields.
    // I think its validated elsewhere, but does not hurt to ensure it here.
    baseFields.forEach((field) => {
        (0, common_1.finleyAssert)(!field.includes('.'), `baseField [${field}] cannot contain "."`);
    });
    dynamicIntermediateFields.forEach((field) => {
        (0, common_1.finleyAssert)(!field.includes('.'), `dynamicIntermediateField [${field}] cannot contain "."`);
    });
    const baseFieldVars = baseFields.map((key) => `${BASE_FIELD_PREFIX}.${key}`);
    const dynamicFieldVars = dynamicIntermediateFields.map((key) => `${DYNAMIC_INTERMEDIATE_PREFIX}.${key}`);
    const documentationFieldVars = documentationFields.map((key) => `${DOCUMENTATION_PREFIX}.${key}`);
    const availableVariables = new Set([
        ...baseFieldVars,
        ...dynamicFieldVars,
        ...documentationFieldVars,
    ]);
    const documentationFormulaErrors = formulas.map((formula) => {
        if (ignoreDynamicIntermediateFields) {
            // we want to ignore any missing errors about dynamic intermediate fields
            // if asked to ignore those. This is because we can't verify those fields
            // on tape-builder. But they should be validated on CA.
            // inspect formula variables and add any dynamic intermediate fields
            const { variables } = (0, formula_engine_1.inspectDependencies)(formula);
            variables.forEach((variable) => {
                if (variable.startsWith(DYNAMIC_INTERMEDIATE_PREFIX)) {
                    availableVariables.add(variable);
                }
            });
        }
        const { errorMessages } = (0, formula_engine_1.getFormulaErrors)(formula, availableVariables);
        return {
            errorMessages,
            formula,
        };
    });
    // filter out any formulas that have no errors
    const errors = documentationFormulaErrors.filter((error) => error.errorMessages.length > 0);
    return errors;
};
exports.checkAssetDocumentationFormulaErrors = checkAssetDocumentationFormulaErrors;
// filles in the receivable field result with the receivable tape value.
const getPartialFieldResult = ({ fieldConfig, receivable, }) => {
    const [domain, fieldName] = fieldConfig.receivableResponseTapeField.split('.');
    const loanTapeValue = domain === 'baseFields'
        ? receivable.baseFields[fieldName]
        : receivable.dynamicIntermediateFields[fieldName];
    return {
        field: fieldName,
        loanTapeValue: String(loanTapeValue), // does this need to be stringified?
        formula: fieldConfig.comparisonFormula,
        result: null,
    };
};
exports.getPartialFieldResult = getPartialFieldResult;
const createAssetDocumentationEvaluator = (fieldConfigs) => {
    const formulaStrings = fieldConfigs.map((field) => field.comparisonFormula);
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, formulaStrings, undefined);
    // TODO:@asset_verification: optimize to set only variables referenced by the formula.
    const setVariables = ({ receivable, document, }) => {
        // documentation.field
        // baseFields.field
        // dynamicIntermediateFields.field
        // document has following format.
        // {
        //   "fields": [{
        //     "fieldName": "amount",
        //     "raw": "$650,000",
        //     "error": null,
        //     "value": 650000,
        //     "confidence": 96
        //   },
        //   {
        //     "fieldName": "company_name",
        //     "raw": "some_name",
        //     "error": null,
        //     "value": "some_name",
        //     "confidence": 96
        //   }]
        // }
        //
        // we only need to pick the value from this object.
        const documentationValues = document.fields.reduce((acc, fieldDocumentation) => {
            const { fieldName, value } = fieldDocumentation;
            // eslint-disable-next-line no-param-reassign
            acc[fieldName] = value;
            return acc;
        }, {});
        const inputs = {
            [BASE_FIELD_PREFIX]: receivable.baseFields,
            [DYNAMIC_INTERMEDIATE_PREFIX]: receivable.dynamicIntermediateFields,
            [DOCUMENTATION_PREFIX]: documentationValues,
        };
        formulaEngine.setVariables(inputs, { flatten: true });
        const evaluateConfig = (fieldConfig) => {
            const { comparisonFormula, receivableDocumentionField } = fieldConfig;
            const verificationResult = formulaEngine.evaluateBoolean(comparisonFormula);
            const documentField = document.fields.find(({ fieldName }) => fieldName === receivableDocumentionField);
            if (!documentField) {
                const availableFields = document.fields.map((field) => field.fieldName);
                (0, common_1.finleyAssert)(documentField, `Unable to find document field: ${receivableDocumentionField}.
          [AvailableFields:${availableFields}],
          [externalReceivableId: ${receivable.externalReceivableId}]
          [FinleyReceivableId:${receivable.finleyReceivableId}]`);
            }
            const fieldResult = {
                docSanitizedValue: String(documentField.value), // @jen: does this need to be string?
                docRawValue: documentField.raw || 'not_available',
                verificationResult,
            };
            return {
                ...(0, exports.getPartialFieldResult)({ fieldConfig, receivable }),
                result: fieldResult,
            };
        };
        return {
            evaluateConfig,
        };
    };
    return {
        setVariables,
    };
};
exports.createAssetDocumentationEvaluator = createAssetDocumentationEvaluator;
// returns a partial verification result with only loan-tape-data.
const getPartialVerificationResult = (assetVerificationConfig, receivable, otherAttributes) => {
    const fields = assetVerificationConfig.fields.map((fieldConfig) => (0, exports.getPartialFieldResult)({ fieldConfig, receivable }));
    const result = {
        ...otherAttributes,
        verificationResult: {
            schemaVersion: 1,
            fields,
        },
        passed: false,
        generatedDocumentationId: null,
        finleyReceivableId: receivable.finleyReceivableId,
        creditFacilityReceivable: receivable,
    };
    return result;
};
// executes given receivables against given documentation and generates results.
const executeAssetVerification = async ({ testReceivables, assetVerificationConfig, otherAttributes = {}, }) => {
    const { setVariables } = (0, exports.createAssetDocumentationEvaluator)(assetVerificationConfig.fields);
    const results = testReceivables.map(({ receivable, latestGeneratedDocumentation }) => {
        if (!latestGeneratedDocumentation) {
            // there was no generated document entry, lets still generate the result which show only loan-tape-data.
            return getPartialVerificationResult(assetVerificationConfig, receivable, otherAttributes);
        }
        try {
            (0, common_1.finleyAssert)(finley_types_1.typeguards.assetVerification.isAssetVerificationStoredDocument(latestGeneratedDocumentation.document), `executeAssetVerification: isAssetVerificationStoredDocument failed for [externalReceivableId:${receivable.externalReceivableId}]`);
            // known issue with our CI compilation
            // ts type checks behaves differently than in local environment
            // here we already use typeguard above to ensure that document is of type AssetVerification.StoredDocumentation.Document, but found consistent failure in CI run.
            const document = latestGeneratedDocumentation.document;
            const evaluator = setVariables({ receivable, document });
            const fields = assetVerificationConfig.fields.map((fieldConfig) => {
                return evaluator.evaluateConfig(fieldConfig);
            });
            const verificationResult = {
                schemaVersion: 1,
                fields,
            };
            const passed = fields.every((field) => field.result && field.result.verificationResult);
            // TODO: in case of failure to evaluateConfig,
            // still create a result with only loan-tape-data.
            const result = {
                ...otherAttributes,
                verificationResult,
                passed,
                generatedDocumentationId: latestGeneratedDocumentation.id,
                finleyReceivableId: receivable.finleyReceivableId,
                creditFacilityReceivable: receivable,
            };
            return result;
        }
        catch (error) {
            // even if formula evaluation for a single receivable fails. (This can happen in cases where types are different from expected types in formula)
            // we still want to continue evaluating rest of the receivables. For the receivable that failed formula evaluation, we will store failed result with loan-tape only values.
            (0, common_1.debugLog)(`Error in executeAssetVerification: for [finleyReceivableId:${receivable.finleyReceivableId}] [externalReceivableId:${receivable.externalReceivableId}]`, error);
            return getPartialVerificationResult(assetVerificationConfig, receivable, otherAttributes);
        }
    });
    return results;
};
exports.executeAssetVerification = executeAssetVerification;
const findReceivableDocumentation = (finleyReceivableId, documentationReceivables) => {
    const matchingDocumentation = documentationReceivables.find((doc) => doc.finleyReceivableId === finleyReceivableId);
    (0, common_1.finleyAssert)(matchingDocumentation, `findReceivableDocumentation: receivable not found. [finleyReceivableId: ${finleyReceivableId}]`);
    const { documentations: internalDocumentations } = matchingDocumentation;
    // among given documents find latest raw.
    const latestDocumentation = internalDocumentations.length > 0
        ? internalDocumentations.reduce((acc, curr) => {
            return acc.raw.created_at > curr.raw.created_at ? acc : curr;
        }, internalDocumentations[0])
        : null;
    // find latest generated document attached to latestRaw
    const latestGeneratedDocumentation = latestDocumentation
        ? latestDocumentation.latestGenerated
        : null;
    // we need to return all documentations with timestamps for caller to render.
    // prepare that for output.
    const documentations = internalDocumentations.map(({ raw }) => {
        const generatedDocumentation = latestGeneratedDocumentation &&
            raw.id === latestGeneratedDocumentation.id
            ? {
                id: latestGeneratedDocumentation.id,
                timestamp: latestGeneratedDocumentation.created_at.getTime(),
                note: latestGeneratedDocumentation.note,
            }
            : null;
        const documentation = {
            rawId: raw.id,
            timestamp: raw.created_at.getTime(),
            generatedDocumentation,
        };
        return documentation;
    });
    return {
        documentations,
        latestGeneratedDocumentation,
        latestRawDocumentation: latestDocumentation?.raw || null,
    };
};
// TODO:@asset_verification: remove this method used by finleyCore,
// once we start using results from tables generated by BBPL.
const generateReceivableDocumentationReport = async ({ receivables, assetVerificationConfig, documentationReceivables, reportingDate, }) => {
    const testReceivables = receivables
        .map((receivable) => {
        const { latestGeneratedDocumentation } = findReceivableDocumentation(receivable.finleyReceivableId, documentationReceivables);
        return {
            receivable,
            latestGeneratedDocumentation,
        };
    })
        .filter(common_1.typeguards.isNotNullOrUndefined);
    const allResults = await (0, exports.executeAssetVerification)({
        testReceivables,
        assetVerificationConfig,
    });
    const reportReceivables = allResults.map((executedResult) => {
        const { finleyReceivableId, verificationResult: { fields }, } = executedResult;
        const { documentations, latestRawDocumentation, latestGeneratedDocumentation, } = findReceivableDocumentation(finleyReceivableId, documentationReceivables);
        const verificationStatus = common_1.assetVerification.getVerificationStatus({
            raw: latestRawDocumentation,
            generated: latestGeneratedDocumentation,
            fields,
        });
        const externalReceivableId = receivables.find((receivable) => receivable.finleyReceivableId === finleyReceivableId)?.externalReceivableId || 'not_available';
        return {
            finleyReceivableId,
            externalReceivableId,
            documentations,
            verificationStatus,
            fields,
            sampleConfigs: [],
            sampled: false,
        };
    });
    const baseFieldConfigs = assetVerificationConfig.fields.map((fieldConfig) => {
        const [, fieldName] = fieldConfig.receivableResponseTapeField.split('.');
        return {
            field: fieldName,
            type: 'string',
        };
    });
    const report = {
        reportingDate,
        receivables: reportReceivables,
        baseFieldConfigs,
    };
    (0, common_1.finleyAssert)(finley_types_1.typeguards.assetVerification.isAssetVerificationReport(report), 'generateReceivableDocumentationReport:Expected asset verification report to be valid');
    return report;
};
exports.generateReceivableDocumentationReport = generateReceivableDocumentationReport;
const constructHistoricalAssetVerificationReport = async ({ receivables, receivableDocumentationReport, }) => {
    const receivableVerificationResultMap = {};
    receivableDocumentationReport.receivables.forEach((receivable) => {
        receivableVerificationResultMap[receivable.finleyReceivableId] =
            receivable.verificationStatus;
    });
    const historicalAssetVerificationDataMap = {};
    receivables.forEach((receivable) => {
        (0, common_1.finleyAssert)(receivable.assignmentFields?.assignedAt, new common_1.error.FinleyError('Expected receivable to have non-null assignedAt to construct historical asset verification data', {
            metadata: {
                logMeta: {
                    receivable,
                },
            },
        }));
        const assignedAtDate = common_1.date.datetimeToFullDateStr(luxon_1.DateTime.fromISO(receivable.assignmentFields.assignedAt));
        const historicalData = historicalAssetVerificationDataMap[assignedAtDate] ?? {
            assignedAtDate,
            totalAssignedAtExposure: 0,
            totalUntestedAssignedAtExposure: 0,
            totalUnverifiedFailureAssignedAtExposure: 0,
            totalVerifiedFailureAssignedAtExposure: 0,
            totalVerifiedPassAssignedAtExposure: 0,
            totalReceivableCount: 0,
            verifiedPassReceivableCount: 0,
            untestedReceivableCount: 0,
            unverifiedFailureReceivableCount: 0,
            verifiedFailureReceivableCount: 0,
        };
        // 3/29/24: purchasePrice is used as proxy for exposure at time of assignment,
        // currently it is always set to that; revisit this if that changes
        const receivableAssignmentExposure = receivable.assignmentFields.purchasePrice ?? 0;
        historicalData.totalAssignedAtExposure += receivableAssignmentExposure;
        historicalData.totalReceivableCount += 1;
        const verificationResult = receivableVerificationResultMap[receivable.finleyReceivableId];
        (0, common_1.finleyAssert)(verificationResult, new common_1.error.FinleyError('Expected asset verification result to exist for receivable', {
            metadata: {
                logMeta: {
                    receivable,
                },
            },
        }));
        switch (verificationResult) {
            case 'Untested':
                historicalData.totalUntestedAssignedAtExposure += receivableAssignmentExposure;
                historicalData.untestedReceivableCount += 1;
                break;
            case 'Passed':
                historicalData.totalVerifiedPassAssignedAtExposure += receivableAssignmentExposure;
                historicalData.verifiedPassReceivableCount += 1;
                break;
            case 'TextractFailed':
                historicalData.totalUntestedAssignedAtExposure += receivableAssignmentExposure;
                historicalData.untestedReceivableCount += 1;
                break;
            case 'UnverifiedFailure':
                historicalData.totalUnverifiedFailureAssignedAtExposure += receivableAssignmentExposure;
                historicalData.unverifiedFailureReceivableCount += 1;
                break;
            case 'VerifiedFailure':
                historicalData.totalVerifiedFailureAssignedAtExposure += receivableAssignmentExposure;
                historicalData.verifiedFailureReceivableCount += 1;
                break;
            default:
                throw (0, common_1.finleyAssertUnreachable)(verificationResult);
        }
        historicalAssetVerificationDataMap[assignedAtDate] = historicalData;
    });
    return Object.values(historicalAssetVerificationDataMap);
};
exports.constructHistoricalAssetVerificationReport = constructHistoricalAssetVerificationReport;
//# sourceMappingURL=assetDocumentationEvaluator.js.map